// ./pdfSlice.ts
import { createSlice, PayloadAction, nanoid } from '@reduxjs/toolkit';
import { RootState } from '../store';

interface PdfFile {
    id: string;
    filename: string;
}

interface ChatPdfFiles {
    [chatGroupId: string]: PdfFile[];
}

interface PdfState {
    filesByChat: ChatPdfFiles;
    currentChatFiles: PdfFile[]; // Current chat's files for easy access
}

// Load from localStorage on initialization
const loadPdfState = (): ChatPdfFiles => {
    try {
        const saved = localStorage.getItem('pdf_files_by_chat');
        return saved ? JSON.parse(saved) : {};
    } catch (error) {
        console.error('Error loading PDF state from localStorage:', error);
        return {};
    }
};

const initialState: PdfState = {
    filesByChat: loadPdfState(),
    currentChatFiles: [],
};

// Helper function to save to localStorage
const savePdfState = (filesByChat: ChatPdfFiles) => {
    try {
        localStorage.setItem('pdf_files_by_chat', JSON.stringify(filesByChat));
    } catch (error) {
        console.error('Error saving PDF state to localStorage:', error);
    }
};

const pdfSlice = createSlice({
    name: 'pdf',
    initialState,
    reducers: {
        // Adds a file entry to a specific chat group
        addPdf: (state, action: PayloadAction<{ filename: string; chatGroupId: string }>) => {
            const { filename, chatGroupId } = action.payload;
            const newFile = {
                id: nanoid(),
                filename,
            };
            
            // Initialize chat group if it doesn't exist
            if (!state.filesByChat[chatGroupId]) {
                state.filesByChat[chatGroupId] = [];
            }
            
            // Add file to the specific chat group
            state.filesByChat[chatGroupId].push(newFile);
            
            // Update current chat files if this is the active chat
            if (state.filesByChat[chatGroupId]) {
                state.currentChatFiles = [...state.filesByChat[chatGroupId]];
            }
            
            // Save to localStorage
            savePdfState(state.filesByChat);
            
            console.log('addPdf Reducer Invoked:', { newFile, chatGroupId });
        },
        
        // Removes a file entry by its unique ID from a specific chat
        deletePdf: (state, action: PayloadAction<{ fileId: string; chatGroupId: string }>) => {
            const { fileId, chatGroupId } = action.payload;
            if (state.filesByChat[chatGroupId]) {
                state.filesByChat[chatGroupId] = state.filesByChat[chatGroupId].filter(
                    file => file.id !== fileId
                );
                // Update current chat files
                state.currentChatFiles = [...state.filesByChat[chatGroupId]];
                // Save to localStorage
                savePdfState(state.filesByChat);
            }
        },
        
        // Deletes all file entries for a specific chat
        deleteAllPdfForChat: (state, action: PayloadAction<string>) => {
            const chatGroupId = action.payload;
            delete state.filesByChat[chatGroupId];
            state.currentChatFiles = [];
            // Save to localStorage
            savePdfState(state.filesByChat);
        },
        
        // Deletes all file entries (for reset functionality)
        deleteAllPdf: (state) => {
            state.filesByChat = {};
            state.currentChatFiles = [];
            // Clear localStorage
            localStorage.removeItem('pdf_files_by_chat');
        },
        
        // Set files for a specific chat group (for loading from backend)
        setChatPdfFiles: (state, action: PayloadAction<{ chatGroupId: string; files: PdfFile[] }>) => {
            const { chatGroupId, files } = action.payload;
            state.filesByChat[chatGroupId] = files;
            // Update current chat files if this is the active chat
            if (state.filesByChat[chatGroupId]) {
                state.currentChatFiles = [...state.filesByChat[chatGroupId]];
            }
            // Save to localStorage
            savePdfState(state.filesByChat);
        },
        
        // Switch to a different chat's PDF files
        switchToChatPdfs: (state, action: PayloadAction<string>) => {
            const chatGroupId = action.payload;
            state.currentChatFiles = state.filesByChat[chatGroupId] || [];
        },
        
        // Clear current chat files (when switching to a chat with no PDFs)
        clearCurrentChatPdfs: (state) => {
            state.currentChatFiles = [];
        },
    },
});

export const { 
    addPdf, 
    deletePdf, 
    deleteAllPdf, 
    deleteAllPdfForChat,
    setChatPdfFiles,
    switchToChatPdfs,
    clearCurrentChatPdfs
} = pdfSlice.actions;

export default pdfSlice.reducer;

// Selector to get the current chat's files
export const selectCurrentChatPdfFiles = (state: RootState): PdfFile[] => state.pdf.currentChatFiles;

// Selector to get files for a specific chat
export const selectChatPdfFiles = (chatGroupId: string) => (state: RootState): PdfFile[] => 
    state.pdf.filesByChat[chatGroupId] || [];